import { alpha, createTheme, type Shadows } from "@mui/material/styles";
import { darkTokens, lightTokens, type GeoAnalyticsThemeMode } from "@/theme/tokens";
import {
  geoDetailDecorOpacity,
  geoVisualAssetOpacity,
  getGeoVisualAsset
} from "@/theme/visualAssets";

const noShadows = Array(25).fill("none") as Shadows;

export function createGeoAnalyticsTheme(mode: GeoAnalyticsThemeMode) {
  const tokens = mode === "dark" ? darkTokens : lightTokens;
  const detailDecorAsset = getGeoVisualAsset("detail", mode);
  const detailDecorBackground = `var(--geo-detail-decor-image, url("${detailDecorAsset}"))`;
  const detailDecorOpacity = geoDetailDecorOpacity[mode];
  const readablePrimary = tokens.action.primaryHover;
  const readableSecondary = tokens.action.secondaryHover;
  const decorVariantStyles = {
    "--geo-detail-decor-image": `url("${detailDecorAsset}")`,
    "--geo-detail-decor-opacity": detailDecorOpacity,
    "--geo-detail-decor-position": "right center",
    "--geo-detail-decor-size": "min(680px, 78%) auto",
    "--geo-detail-decor-mask":
      "linear-gradient(90deg, transparent 0%, transparent 58%, rgba(0, 0, 0, 0.1) 78%, rgba(0, 0, 0, 0.34) 100%)"
  };
  const detailDecorPseudoElement = {
    content: '""',
    position: "absolute",
    inset: 0,
    zIndex: 0,
    backgroundImage: detailDecorBackground,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "var(--geo-detail-decor-position)",
    backgroundSize: "var(--geo-detail-decor-size)",
    opacity: "var(--geo-detail-decor-opacity)",
    maskImage: "var(--geo-detail-decor-mask)",
    WebkitMaskImage: "var(--geo-detail-decor-mask)",
    pointerEvents: "none"
  };
  const liftContentAboveDecor = {
    position: "relative",
    zIndex: 1
  };

  return createTheme({
    palette: {
      mode,
      primary: {
        main: tokens.action.primary,
        dark: tokens.action.primaryHover,
        light: tokens.action.primarySoft,
        contrastText: tokens.action.primaryContrast
      },
      secondary: {
        main: tokens.action.secondary,
        dark: tokens.action.secondaryHover,
        light: tokens.action.secondarySoft,
        contrastText: tokens.action.secondaryContrast
      },
      background: {
        default: tokens.background.default,
        paper: tokens.background.surface
      },
      text: {
        primary: tokens.text.primary,
        secondary: tokens.text.secondary,
        disabled: tokens.text.disabled
      },
      divider: tokens.border.divider,
      success: { main: tokens.status.success },
      warning: { main: tokens.status.warning },
      error: { main: tokens.status.error },
      info: { main: tokens.status.info },
      action: {
        hover: alpha(tokens.action.primarySoft, mode === "dark" ? 0.48 : 0.72),
        selected: alpha(tokens.action.primarySoft, mode === "dark" ? 0.72 : 0.96),
        disabled: tokens.text.disabled,
        disabledBackground: alpha(tokens.text.disabled, mode === "dark" ? 0.18 : 0.16)
      }
    },
    shape: {
      borderRadius: 6
    },
    shadows: noShadows,
    typography: {
      fontFamily:
        'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontSize: "2rem", fontWeight: 700, letterSpacing: 0 },
      h2: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: 0 },
      h3: { fontSize: "1.25rem", fontWeight: 700, letterSpacing: 0 },
      button: { fontWeight: 700, letterSpacing: 0, textTransform: "none" }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            direction: "ltr"
          },
          body: {
            backgroundColor: tokens.background.default,
            transition: "background-color 420ms ease, color 420ms ease"
          },
          ".geo-page-header": {
            "--geo-header-image": `url("${getGeoVisualAsset("header", mode)}")`,
            "--geo-header-opacity": geoVisualAssetOpacity[mode]
          },
          "[data-geo-auto-translated='true']": {
            unicodeBidi: "plaintext"
          },
          a: {
            color: readablePrimary,
            textDecorationColor: alpha(readablePrimary, 0.42),
            textUnderlineOffset: 3
          },
          "a:hover": {
            color: readableSecondary,
            textDecorationColor: alpha(readableSecondary, 0.72)
          }
        }
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true
        },
        styleOverrides: {
          root: {
            position: "relative",
            overflow: "hidden",
            minHeight: 40,
            borderRadius: 8,
            transition:
              "transform 180ms ease, background-color 220ms ease, border-color 220ms ease",
            "&:hover": {
              transform: "translateY(-1px)"
            },
            "&:active": {
              transform: "translateY(0)"
            },
            "&.Mui-disabled": {
              transform: "none"
            }
          },
          contained: {
            border: "1px solid transparent",
            background: tokens.action.primary,
            boxShadow: "none",
            color: tokens.action.primaryContrast,
            textShadow: "0 1px 1px rgba(0,0,0,0.22)",
            "&:hover": {
              background: tokens.action.primaryHover,
              boxShadow: "none",
              color: tokens.action.primaryContrast
            },
            "&.Mui-disabled": {
              background: alpha(tokens.text.disabled, mode === "dark" ? 0.18 : 0.16),
              color: alpha(tokens.text.primary, mode === "dark" ? 0.44 : 0.42),
              textShadow: "none"
            }
          },
          outlined: {
            backdropFilter: "blur(8px) saturate(120%)",
            backgroundColor:
              mode === "dark"
                ? alpha(tokens.background.surface, 0.72)
                : alpha(tokens.background.surface, 0.86),
            borderColor: alpha(tokens.border.default, 0.82),
            color: mode === "dark" ? tokens.text.primary : tokens.action.primaryHover,
            "&:hover": {
              borderColor: tokens.action.primary,
              backgroundColor:
                mode === "dark"
                  ? alpha(tokens.action.primaryHover, 0.18)
                  : alpha(tokens.action.primarySoft, 0.9),
              color: mode === "dark" ? tokens.text.primary : tokens.action.primaryHover
            },
            "&.Mui-disabled": {
              borderColor: alpha(tokens.text.disabled, mode === "dark" ? 0.24 : 0.28),
              color: alpha(tokens.text.disabled, mode === "dark" ? 0.62 : 0.72)
            }
          },
          text: {
            color: tokens.action.primaryHover,
            "&:hover": {
              backgroundColor: alpha(tokens.action.primarySoft, mode === "dark" ? 0.4 : 0.64)
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: readablePrimary,
            fontWeight: 900,
            textDecorationColor: alpha(readablePrimary, 0.42),
            textUnderlineOffset: 3,
            transition: "color 180ms ease, text-decoration-color 180ms ease",
            "&:hover": {
              color: readableSecondary,
              textDecorationColor: alpha(readableSecondary, 0.72)
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: tokens.background.surface,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            backdropFilter: "none",
            borderColor: alpha(tokens.border.default, 0.82),
            boxShadow: "none",
            transition: "background-color 320ms ease, border-color 320ms ease",
            "&.MuiPaper-outlined": {
              ...decorVariantStyles,
              position: "relative",
              overflow: "hidden",
              backgroundImage: "none",
              backgroundColor: tokens.background.surface,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              backgroundSize: "cover",
              "&.geo-detail-decor::before, &.geo-decor-auto::before, &.geo-detail-decor-subtle::before, &.geo-detail-decor-hero::before":
                detailDecorPseudoElement,
              "&.geo-no-detail-decor::before": {
                content: "none",
                display: "none"
              },
              "&.geo-detail-decor > *, &.geo-decor-auto > *, &.geo-detail-decor-subtle > *, &.geo-detail-decor-hero > *":
                liftContentAboveDecor,
              "&.geo-detail-decor-subtle": {
                "--geo-detail-decor-opacity": mode === "dark" ? 0.025 : 0.03,
                "--geo-detail-decor-size": "min(560px, 72%) auto"
              },
              "&.geo-detail-decor-hero": {
                "--geo-detail-decor-opacity": mode === "dark" ? 0.08 : 0.09,
                "--geo-detail-decor-position": "center",
                "--geo-detail-decor-size": "cover"
              }
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            ...decorVariantStyles,
            position: "relative",
            overflow: "hidden",
            backgroundImage: "none",
            backgroundColor: tokens.background.surface,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "cover",
            border: `1px solid ${alpha(tokens.border.default, 0.82)}`,
            borderRadius: 8,
            "&.geo-detail-decor::before, &.geo-decor-auto::before, &.geo-detail-decor-subtle::before, &.geo-detail-decor-hero::before":
              detailDecorPseudoElement,
            "&.geo-no-detail-decor::before": {
              content: "none",
              display: "none"
            },
            "&.geo-detail-decor > *, &.geo-decor-auto > *, &.geo-detail-decor-subtle > *, &.geo-detail-decor-hero > *":
              liftContentAboveDecor
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundRepeat: "initial",
            backgroundPosition: "initial",
            backgroundSize: "initial"
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: "blur(18px) saturate(160%)",
            backgroundColor:
              mode === "dark"
                ? alpha(tokens.background.surface, 0.78)
                : alpha(tokens.background.surface, 0.86),
            color: tokens.text.primary,
            transition: "background-color 320ms ease, border-color 320ms ease"
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: "none",
            backgroundColor: tokens.background.surface,
            backdropFilter: "none"
          }
        }
      },
      MuiSvgIcon: {
        defaultProps: {
          color: "inherit",
          fontSize: "medium"
        },
        styleOverrides: {
          fontSizeSmall: { fontSize: 20 },
          fontSizeMedium: { fontSize: 22 },
          fontSizeLarge: { fontSize: 32 }
        }
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { color: tokens.text.secondary }
        }
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.color === "default" && { color: tokens.text.secondary }),
            transition:
              "transform 180ms ease, background-color 220ms ease, border-color 220ms ease, color 220ms ease",
            "&:hover": {
              transform: "translateY(-1px)",
              backgroundColor: alpha(tokens.action.primarySoft, mode === "dark" ? 0.48 : 0.76),
              ...(ownerState.color === "default" && { color: tokens.action.primaryHover })
            },
            "&:active": {
              transform: "translateY(0)"
            }
          })
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor:
              mode === "dark"
                ? alpha(tokens.background.surface, 0.9)
                : alpha(tokens.background.surface, 0.92),
            backdropFilter: "none",
            transition: "background-color 240ms ease, border-color 240ms ease"
          },
          notchedOutline: {
            borderColor: alpha(tokens.border.default, 0.86)
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 48
          },
          indicator: {
            height: 3,
            borderRadius: 3,
            backgroundColor: tokens.action.primary
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 48,
            paddingInline: 16,
            fontWeight: 800,
            textTransform: "none",
            color: tokens.text.secondary,
            "&.Mui-selected": {
              color: tokens.text.primary
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: tokens.border.divider,
            padding: "14px 12px"
          },
          head: {
            color: tokens.text.secondary,
            fontWeight: 800,
            backgroundColor: alpha(tokens.background.default, mode === "dark" ? 0.34 : 0.52)
          }
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backdropFilter: "none",
            backgroundColor:
              mode === "dark"
                ? alpha(tokens.background.surface, 0.86)
                : alpha(tokens.background.surface, 0.92)
          }
        }
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: tokens.text.secondary,
            minWidth: 64,
            "&.Mui-selected": {
              color: mode === "dark" ? tokens.action.primaryHover : tokens.action.primary,
              fontWeight: 800
            },
            "& .MuiBottomNavigationAction-label": {
              fontWeight: 800
            },
            "& .MuiBottomNavigationAction-label.Mui-selected": {
              fontSize: "0.75rem"
            }
          }
        }
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-track": {
              transition: "background-color 260ms ease, opacity 260ms ease"
            },
            "& .MuiSwitch-thumb": {
              transition: "transform 260ms cubic-bezier(0.2, 0.9, 0.2, 1)"
            }
          }
        }
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(tokens.action.primarySoft, mode === "dark" ? 0.52 : 0.84),
            "&::after": {
              background: `linear-gradient(90deg, transparent, ${alpha(
                tokens.action.secondarySoft,
                mode === "dark" ? 0.32 : 0.78
              )}, transparent)`
            }
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            position: "relative",
            isolation: "isolate",
            overflow: "hidden",
            minHeight: 32,
            alignSelf: "flex-start",
            width: "fit-content",
            maxWidth: "100%",
            borderRadius: 999,
            fontWeight: 900,
            letterSpacing: 0,
            backdropFilter: "none",
            boxShadow: "none",
            transition:
              "background-color 220ms ease, border-color 220ms ease, color 220ms ease, transform 180ms ease",
            "&::before": { content: "none" },
            "& .MuiChip-label": {
              px: 1.35
            },
            "& .MuiChip-icon": {
              ml: 1.1
            },
            "& .MuiChip-icon.MuiSvgIcon-colorInherit": {
              color: "currentColor"
            },
            "&:hover": {
              transform: "translateY(-1px)"
            },
            "&.MuiChip-sizeSmall": {
              minHeight: 28,
              "& .MuiChip-label": {
                px: 1.15
              }
            },
            "&.MuiChip-filledPrimary": {
              background: tokens.action.primary,
              color: tokens.action.primaryContrast,
              "& .MuiChip-deleteIcon": {
                color: alpha(tokens.action.primaryContrast, 0.82)
              }
            },
            "&.MuiChip-filledSecondary": {
              background: `linear-gradient(135deg, ${tokens.action.secondary}, ${tokens.action.primary})`,
              color: tokens.action.secondaryContrast
            },
            "&.MuiChip-outlinedPrimary": {
              borderColor: alpha(tokens.action.primary, mode === "dark" ? 0.72 : 0.46),
              borderWidth: 1,
              backgroundColor: alpha(tokens.action.primarySoft, mode === "dark" ? 0.78 : 0.84),
              color: readablePrimary
            },
            "&.MuiChip-outlinedSecondary": {
              borderColor: alpha(tokens.action.secondary, mode === "dark" ? 0.68 : 0.46),
              borderWidth: 1,
              backgroundColor: alpha(tokens.action.secondarySoft, mode === "dark" ? 0.76 : 0.84),
              color: readableSecondary
            }
          }
        }
      },
      MuiTooltip: {
        defaultProps: {
          arrow: true
        }
      }
    }
  });
}
